import { useState } from 'react';
import { formatDistanceToNow, minutesToMilliseconds } from 'date-fns';
import {
  DashboardModalWithCloseButton,
  ModalContent,
  StatsPanel,
} from '@/shared/components/dashboards/Modal';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import TokenInputField from '@/shared/components/TokenInputField';
import WalletActionButton from '@/shared/components/WalletActionButton';
import useAuction from '@/shared/hooks/useAuction';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useUndelegateFlip from '@/shared/hooks/useUndelegateFlip';
import GiveCoinsIcon from '@/shared/icons/flip-ui-kit/small/GiveCoins';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import type TokenAmount from '@/shared/utils/TokenAmount';

export default function UndelegateModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: VoidFunction;
}) {
  const { auction } = useAuction();
  const [amount, setAmount] = useState<TokenAmount>();
  const [error, setError] = useState<string>();
  const [inputHasError, setInputHasError] = useState(false);
  const { nextEpoch, redeemableBalance } = useEvmScAccount(true);

  const delegatedBalance = nextEpoch?.delegatedBalance;

  const waitTill = auction
    ? formatDistanceToNow(auction.endsAt.valueOf() + minutesToMilliseconds(20))
    : 'until after the end of the current auction';

  const { mutate: undelegate, isPending } = useUndelegateFlip({ onSuccess: onClose });

  const reset = () => {
    setAmount(undefined);
    setError(undefined);
  };

  return (
    <DashboardModalWithCloseButton open={open} onClose={onClose} afterLeave={reset}>
      <ModalContent
        title={`Undelegate ${FLIP_SYMBOL}`}
        titleSize="large"
        description={<>Undelegating makes your {FLIP_SYMBOL} available for withdrawal.</>}
        buttons={
          <WalletActionButton
            chainId={chainflipAssetMap.Flip.chain.id}
            requireConnection
            fullWidth
            disabled="ifLoading"
            loading={isPending && 'iconOnly'}
            onClick={() => {
              if (inputHasError) return;
              if (!amount || amount.eq(0)) {
                setError('Please enter an amount to undelegate');
                return;
              }

              undelegate(amount.eq(delegatedBalance) ? undefined : amount);
            }}
          >
            Undelegate
          </WalletActionButton>
        }
      >
        <div className="flex flex-col gap-y-6">
          <Callout type="info">
            After undelegating your {FLIP_SYMBOL}, you will need to wait{' '}
            <span className="font-aeonikBold">{waitTill}</span> to initiate a withdrawal.
          </Callout>
          <TokenInputField
            token={chainflipAssetMap.Flip}
            maxAmount={delegatedBalance}
            balance={delegatedBalance}
            value={amount}
            onChange={setAmount}
            error={error}
            setHasError={setInputHasError}
            noBalanceLabel
          />
          <StatsPanel
            items={[
              {
                Icon: GiveCoinsIcon,
                label: 'Total Delegation',
                flipAmount: delegatedBalance,
                tooltipContent: `The total amount of ${FLIP_SYMBOL} you currently have delegated across Operators.`,
              },
              {
                Icon: GiveCoinsIcon,
                label: 'Available for withdrawal',
                flipAmount: redeemableBalance,
                tooltipContent: `The amount of ${FLIP_SYMBOL} available to withdraw after undelegation. Once withdrawn, it returns to your Ethereum wallet.`,
              },
            ]}
          />
        </div>
      </ModalContent>
    </DashboardModalWithCloseButton>
  );
}
